/**
 * Route guard for `/admin/*`.
 *
 * Renders the nested route tree (`<Outlet />`) only when:
 *  - `useAuth()` reports an authenticated session, AND
 *  - the locally-decoded `payload.exp` is strictly greater than `now`.
 *
 * Otherwise redirects to `/admin/login` (replace = true so the protected URL
 * is not pushed onto the history stack).
 *
 * The local decode is purely defensive - the backend remains the ultimate
 * authority and will return 401 for any tampered token, which `ApiClient`
 * translates into a forced logout (Req 8.8).
 *
 * Validates: Requirements 4.8, 8.2
 */

import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from './AuthContext';

const LOGIN_PATH = '/admin/login';

interface ProtectedRouteProps {
  /**
   * Optional explicit children. When omitted, `<Outlet />` is rendered so the
   * component can be used as a layout route in `react-router` v6.
   */
  children?: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps): JSX.Element {
  const { token, payload, isAuthenticated } = useAuth();

  // Belt-and-braces: re-check the exp here using the same clock the auth
  // context used. This guards against a stale `isAuthenticated` if a consumer
  // ever bypasses `useAuth()`.
  const expSeconds = payload?.exp;
  const expIsFresh =
    typeof expSeconds === 'number' &&
    Number.isFinite(expSeconds) &&
    expSeconds > Date.now() / 1000;

  if (token === null || !isAuthenticated || !expIsFresh) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

export default ProtectedRoute;
